import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import Common from '../../../shared/Common';
import Skeleton from 'react-loading-skeleton';
import type { IScraperDetails } from '../../../../interfaces/IScraperDetails';
import type { IScraperDataFeed } from '../../../../interfaces/IScraperDataFeed';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Breadcrumb, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useLocalStorage } from 'react-use';

enum GroupingType {
  None = 0,
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
}

enum ShowLast {
  Week = 0,
  Month = 1,
  TwoMonths = 2,
  HalhYear = 3,
  Year = 4,
  TwoYears = 5,
  All = 6,
}

const ScraperDetails = () => {
  const [scraperDetails, setScraperDetails] = useState<IScraperDetails>();
  const [loading, setLoading] = useState<boolean>(false);
  const [dataStartDate, setDataStartDate] = useState<Date>(new Date(new Date().setMonth(new Date().getMonth() - 6)));
  const [dataEndDate, setDataEndDate] = useState<Date>(new Date());
  const [dataBeginningDate, setDataBeginnigDate] = useState<Date>(
    new Date(new Date().setMonth(new Date().getMonth() - 6))
  );
  const [dataCalculationDate, setDataCalculationDate] = useState<Date>(Common.Utils.getPreviousMonthDate(new Date()));
  const [grouping, setGrouping] = useLocalStorage<GroupingType>('ScraperDetails.grouping', GroupingType.Daily);
  const [showLast, setShowLast] = useLocalStorage<ShowLast>('ScraperDetails.showLast', ShowLast.HalhYear);

  const [showCollectedRecords, setShowCollectedRecords] = useLocalStorage<boolean>(
    'ScraperDetails.showCollectedRecords',
    true
  );
  const [showCollectedUniqueRecords, setShowCollectedUniqueRecords] = useLocalStorage<boolean>(
    'ScraperDetails.showCollectedUniqueRecords',
    true
  );

  const [showProcessingTimeInMiliseconds, setShowProcessingTimeInMiliseconds] = useLocalStorage<boolean>(
    'ScraperDetails.showProcessingTimeInMiliseconds',
    true
  );
  const [showGetUniqueNormalizedProductsStat, setShowGetUniqueNormalizedProductsStat] = useLocalStorage<boolean>(
    'ScraperDetails.showGetUniqueNormalizedProductsStat',
    true
  );
  const [showSynchronizeProductsStat, setShowSynchronizeProductsStat] = useLocalStorage<boolean>(
    'ScraperDetails.showSynchronizeProductsStat',
    true
  );
  const [showSynchronizeCategoriesStat, setShowSynchronizeCategoriesStat] = useLocalStorage<boolean>(
    'ScraperDetails.showSynchronizeCategoriesStat',
    true
  );
  const [showSynchronizeLocationsProductInfoStat, setShowSynchronizeLocationsProductInfoStat] =
    useLocalStorage<boolean>('ScraperDetails.showSynchronizeLocationsProductInfoStat', true);

  const [showWebcrawlerCollectingTotalTimeInMilliseconds, setShowWebcrawlerCollectingTotalTimeInMilliseconds] =
    useLocalStorage<boolean>('ScraperDetails.showWebcrawlerCollectingTotalTimeInMilliseconds', true);
  const [showWebcrawlerCollectingAbsoluteTimeInMilliseconds, setShowWebcrawlerCollectingAbsoluteTimeInMilliseconds] =
    useLocalStorage<boolean>('ScraperDetails.showWebcrawlerCollectingAbsoluteTimeInMilliseconds', true);
  const [showWebcrawlerCollectingAverageTimeInMilliseconds, setShowWebcrawlerCollectingAverageTimeInMilliseconds] =
    useLocalStorage<boolean>('ScraperDetails.showWebcrawlerCollectingAverageTimeInMilliseconds', true);

  useEffect(() => {
    let startDate = new Date();
    switch (showLast) {
      case ShowLast.Week:
        startDate = new Date(new Date().setDate(new Date().getDate() - 7));
        break;
      case ShowLast.Month:
        startDate = new Date(new Date().setDate(new Date().getDate() - 30));
        break;
      case ShowLast.TwoMonths:
        startDate = new Date(new Date().setMonth(new Date().getMonth() - 2));
        break;
      case ShowLast.HalhYear:
        startDate = new Date(new Date().setMonth(new Date().getMonth() - 6));
        break;
      case ShowLast.Year:
        startDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
        break;
      case ShowLast.TwoYears:
        startDate = new Date(new Date().setFullYear(new Date().getFullYear() - 2));
        break;
      case ShowLast.All:
        startDate = dataBeginningDate;
        break;
      default:
        break;
    }
    setDataStartDate(startDate);
    if (dataBeginningDate > startDate) {
      setDataStartDate(dataBeginningDate);
    }
  }, [showLast, dataBeginningDate]);

  const fetchIdRef = useRef(0);
  const params = useParams();

  const fetchUserData = useCallback(async (id: string, grouping: number) => {
    setLoading(true);
    const fetchId = ++fetchIdRef.current;

    const response = await Common.authorizedFetch(
      'api/scrapers/GetScraperDetails?locationId=' + id + '&grouping=' + grouping
    );
    if (fetchId === fetchIdRef.current) {
      const data = await response.json();
      if (data.success) {
        setScraperDetails(data.result);
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (scraperDetails) {
      const minDate = Math.min(...(scraperDetails?.dataFeeds?.map((e) => new Date(e.date).getTime()) ?? []));
      const beginnigDate = new Date(minDate);
      setDataBeginnigDate(beginnigDate);
    }
  }, [scraperDetails]);

  useEffect(() => {
    if (params.locationId) {
      fetchUserData(params.locationId, grouping!);
    }
  }, [fetchUserData, params.locationId, grouping]);

  const renderBreadcrumb = () => (
    <Breadcrumb className='mt-2'>
      <Breadcrumb.Item key='level-0' linkAs={Link} linkProps={{ to: '/settings/application/products-scrapers' }}>
        Zbieracze danych
      </Breadcrumb.Item>
      <Breadcrumb.Item key='level-1' active>
        {loading ? 'Ładowanie...' : scraperDetails?.locationName}
      </Breadcrumb.Item>
    </Breadcrumb>
  );

  const renderLegend = (loading: boolean) => (
    <Container>
      <Row className='justify-content-center align-items-start' style={{ gap: '40px', paddingBottom: '5px' }}>
        <Col xs='auto'>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '14px' }}>Początek</label>
            <Form.Control
              disabled={loading}
              type='date'
              value={Common.Utils.getDateString(dataStartDate)}
              min={Common.Utils.getDateString(dataBeginningDate)}
              max={Common.Utils.getDateString(new Date())}
              onChange={(e) => {
                const predictionDate = new Date(e.target.value);
                if (predictionDate > dataCalculationDate) {
                  setDataCalculationDate(predictionDate);
                }
                setDataStartDate(predictionDate);
              }}
              style={{
                width: '120px',
                height: '25px',
                fontSize: '12px',
              }}
            />
          </div>
        </Col>
        <Col xs='auto'>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '14px' }}>Koniec</label>
            <Form.Control
              disabled={loading}
              type='date'
              value={Common.Utils.getDateString(dataEndDate)}
              min={Common.Utils.getDateString(dataBeginningDate)}
              max={Common.Utils.getDateString(new Date())}
              onChange={(e) => {
                const predictionDate = new Date(e.target.value);
                setDataEndDate(predictionDate);
              }}
              style={{
                width: '120px',
                height: '25px',
                fontSize: '12px',
              }}
            />
          </div>
        </Col>
        <Col xs='auto'>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '14px' }}>Pokaż ostatnie</label>
            <Form.Select
              id='show-last-select'
              disabled={loading}
              value={showLast}
              onChange={(e) => {
                const value = Number(e.target.value);
                setShowLast(value as ShowLast);
                setDataEndDate(new Date());
              }}
              style={{
                width: '120px',
                height: '25px',
                fontSize: '12px',
                padding: '2px 4px',
              }}
            >
              <option value={0}>7 dni</option>
              <option value={1}>30 dni</option>
              <option value={2}>2 miesiące</option>
              <option value={3}>6 miesięcy</option>
              <option value={4}>1 rok</option>
              <option value={5}>2 lata</option>
              <option value={6}>wszystko</option>
            </Form.Select>
          </div>
        </Col>
        <Col xs='auto'>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '14px' }}>Grupowanie</label>
            <Form.Select
              disabled={loading}
              value={grouping}
              onChange={(e) => setGrouping(Number(e.target.value) as 0 | 1 | 2 | 3)}
              style={{
                width: '120px',
                height: '25px',
                fontSize: '12px',
                padding: '2px 4px',
              }}
            >
              <option value={GroupingType.None}>Brak</option>
              <option value={GroupingType.Daily}>Jednodniowe</option>
              <option value={GroupingType.Weekly}>Tygodniowe</option>
              <option value={GroupingType.Monthly}>Miesięczne</option>
            </Form.Select>
          </div>
        </Col>
      </Row>
    </Container>
  );

  const generateDataFeedsCounterChart = useCallback(
    (data: IScraperDataFeed[]) => {
      const collectedRecordsCountColor = '#100e92ff';
      const uniqueRecordsCountColor = '#168a33ff';
      const RenderChartLegend = () => (
        <Container>
          <Row xs='auto' className='justify-content-center align-items-center'>
            <Col>
              <Form.Check
                id='show-collected-records-switch'
                label={<div style={{ color: collectedRecordsCountColor }}>Wszystkie</div>}
                style={{ userSelect: 'none' }}
                checked={showCollectedRecords}
                onChange={() => {
                  setShowCollectedRecords(!showCollectedRecords);
                }}
              />
            </Col>
            <Col>
              <Form.Check
                id='show-collected-unique-records-switch'
                label={<div style={{ color: uniqueRecordsCountColor }}>Unikalne</div>}
                style={{ userSelect: 'none' }}
                checked={showCollectedUniqueRecords}
                onChange={() => {
                  setShowCollectedUniqueRecords(!showCollectedUniqueRecords);
                }}
              />
            </Col>
          </Row>
        </Container>
      );
      data = data.filter((d) => new Date(d.date) >= dataStartDate && new Date(d.date) <= dataEndDate);
      const chartData = data.map((element) => {
        const result = {
          date: new Date(element.date),
          collectedRecordsCount: element.completionDate ? element.collectedRecordsCount : undefined,
          uniqueRecordsCount: element.completionDate ? element.uniqueRecordsCount : undefined,
          notCompleted: element.completionDate ? undefined : 0,
        };
        return result;
      });
      return (
        <div className='overflow-auto'>
          <ResponsiveContainer minWidth={600} width='100%' height={400}>
            <ComposedChart
              accessibilityLayer={false}
              data={chartData}
              margin={{
                left: 10,
                right: 25,
                top: 5,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray='1' />
              <XAxis dataKey={'date'} name={'Dzień'} tickFormatter={(e) => Common.Utils.getDateString(new Date(e))} />
              <YAxis />
              {data.length > 0 && (
                <Scatter
                  data={chartData}
                  name={'Nie ukończono'}
                  fill='red'
                  cursor='pointer'
                  dataKey='notCompleted'
                  legendType='none'
                />
              )}
              {showCollectedRecords && (
                <Line
                  type='monotone'
                  dataKey='collectedRecordsCount'
                  name='Liczba rekordów'
                  activeDot={{ r: 6 }}
                  stroke={collectedRecordsCountColor}
                  connectNulls={true}
                />
              )}
              {showCollectedUniqueRecords && (
                <Line
                  type='monotone'
                  dataKey='uniqueRecordsCount'
                  name='Liczba unikalnych rekordów'
                  activeDot={{ r: 6 }}
                  stroke={uniqueRecordsCountColor}
                  connectNulls={true}
                />
              )}
              <Legend content={<RenderChartLegend />} verticalAlign='top' iconSize={10} height={36} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const collectedRecordsCount = payload.find((p) => p.dataKey === 'collectedRecordsCount');
                    const uniqueRecordsCount = payload.find((p) => p.dataKey === 'uniqueRecordsCount');
                    const notCompleted = payload.find((p) => p.dataKey === 'notCompleted');
                    return (
                      <div
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '6px',
                            borderBottom: '1px solid #e0e0e0',
                            paddingBottom: '4px',
                          }}
                        >
                          {new Date(label ?? 0).toLocaleString()}
                        </div>
                        {collectedRecordsCount && (
                          <div style={{ fontSize: '12px', color: collectedRecordsCount.color, marginBottom: '3px' }}>
                            <span style={{ fontWeight: '500' }}>{`${collectedRecordsCount.name}:`}</span>{' '}
                            {collectedRecordsCount.value}
                          </div>
                        )}
                        {uniqueRecordsCount && (
                          <div style={{ fontSize: '12px', color: uniqueRecordsCount.color, marginBottom: '3px' }}>
                            <span style={{ fontWeight: '500' }}>{`${uniqueRecordsCount.name}:`}</span>{' '}
                            {uniqueRecordsCount.value}
                          </div>
                        )}
                        {notCompleted && (
                          <div style={{ fontSize: '12px', color: 'red', marginBottom: '3px' }}>
                            <span style={{ fontWeight: '500' }}>Nieudane pobranie</span>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      );
    },
    [
      dataStartDate,
      dataEndDate,
      showCollectedRecords,
      showCollectedUniqueRecords,
      setShowCollectedRecords,
      setShowCollectedUniqueRecords,
    ]
  );

  const generateDataFeedsTimeChart = useCallback(
    (data: IScraperDataFeed[]) => {
      const processingTimeInMilisecondsColor = '#100e92ff';
      const colors = ['#003f5c', '#7a5195', '#ef5675', '#ffa600', '#ffa600', '#b30f46ff', '#83a6ed', '#8a2be2'];
      data = data.filter((d) => new Date(d.date) >= dataStartDate && new Date(d.date) <= dataEndDate);
      const translations: any = {
        GetUniqueNormalizedProducts: {
          name: 'Nomralizacja unikalnych elementów',
          show: showGetUniqueNormalizedProductsStat,
        },
        SynchronizeProducts: { name: 'Synchronizacja elementów', show: showSynchronizeProductsStat },
        SynchronizeCategories: { name: 'Synchronizacja kategorii', show: showSynchronizeCategoriesStat },
        SynchronizeLocationsProductInfo: {
          name: 'Synchronizacja elementów lokalizacji',
          show: showSynchronizeLocationsProductInfoStat,
        },
      };
      const RenderChartLegend = () => (
        <Container>
          <Row xs='auto' className='justify-content-center align-items-center'>
            <Col>
              <Form.Check
                id='show-processing-time-switch'
                label={<div style={{ color: processingTimeInMilisecondsColor }}>Całkowity</div>}
                style={{ userSelect: 'none' }}
                checked={showProcessingTimeInMiliseconds}
                onChange={() => {
                  setShowProcessingTimeInMiliseconds(!showProcessingTimeInMiliseconds);
                }}
              />
            </Col>
            <Col>
              <Col>
                <Form.Check
                  id='show-GetUniqueNormalizedProducts-time-switch'
                  label={<div style={{ color: colors[0] }}>Normalizacja</div>}
                  style={{ userSelect: 'none' }}
                  checked={showGetUniqueNormalizedProductsStat}
                  onChange={() => {
                    setShowGetUniqueNormalizedProductsStat(!showGetUniqueNormalizedProductsStat);
                  }}
                />
              </Col>
              <Col>
                <Form.Check
                  id='show-SynchronizeProducts-time-switch'
                  label={<div style={{ color: colors[1] }}>Elementy</div>}
                  style={{ userSelect: 'none' }}
                  checked={showSynchronizeProductsStat}
                  onChange={() => {
                    setShowSynchronizeProductsStat(!showSynchronizeProductsStat);
                  }}
                />
              </Col>
            </Col>
            <Col>
              <Col>
                <Form.Check
                  id='show-SynchronizeCategories-switch'
                  label={<div style={{ color: colors[2] }}>Kategorie</div>}
                  style={{ userSelect: 'none' }}
                  checked={showSynchronizeCategoriesStat}
                  onChange={() => {
                    setShowSynchronizeCategoriesStat(!showSynchronizeCategoriesStat);
                  }}
                />
              </Col>
              <Col>
                <Form.Check
                  id='show-SynchronizeLocationsProductInfo-switch'
                  label={<div style={{ color: colors[3] }}>Ceny</div>}
                  style={{ userSelect: 'none' }}
                  checked={showSynchronizeLocationsProductInfoStat}
                  onChange={() => {
                    setShowSynchronizeLocationsProductInfoStat(!showSynchronizeLocationsProductInfoStat);
                  }}
                />
              </Col>
            </Col>
          </Row>
        </Container>
      );
      const steptsDict: any = {};
      const chartData = data.map((element) => {
        const stats: any = {};
        let result = {
          date: new Date(element.date),
          processingTimeInMiliseconds: element.completionDate ? element.processingTimeInMiliseconds : undefined,
          notCompleted: element.completionDate ? undefined : 0,
        };
        if (element.statisticsJson) {
          const steps: any[] = JSON.parse(element.statisticsJson).Steps;
          steps.forEach((step) => {
            if (!(step.Step in steptsDict)) {
              steptsDict[step.Step] = step.ElapsedMiliseconds;
            }
            stats[step.Step] = step.ElapsedMiliseconds;
          });
          result = { ...result, ...stats };
        }
        return result;
      });
      return (
        <div className='overflow-auto'>
          <ResponsiveContainer minWidth={600} width='100%' height={450}>
            <ComposedChart
              accessibilityLayer={false}
              data={chartData}
              margin={{
                left: 10,
                right: 25,
                top: 5,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray='1' />
              <XAxis dataKey={'date'} name={'Dzień'} tickFormatter={(e) => Common.Utils.getDateString(new Date(e))} />
              <YAxis
                width={'auto'}
                tickFormatter={(val) => Common.Utils.getComponentTimestampFromSeconds(Math.round(Number(val) / 1000))}
              />
              {Object.keys(steptsDict).map(
                (key, index) =>
                  key in translations &&
                  translations[key].show && (
                    <Bar
                      key={index}
                      dataKey={key}
                      stackId='a'
                      name={key in translations ? translations[key].name : key}
                      fill={colors[index % colors.length]}
                      legendType='none'
                    />
                  )
              )}
              {showProcessingTimeInMiliseconds && (
                <Line
                  type='monotone'
                  dataKey='processingTimeInMiliseconds'
                  name='Czas przetwarzania'
                  activeDot={{ r: 6 }}
                  stroke={processingTimeInMilisecondsColor}
                  connectNulls={true}
                />
              )}
              <Legend content={<RenderChartLegend />} verticalAlign='top' iconSize={10} height={66} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const processingTimeInMiliseconds = payload.find(
                      (p) => p.dataKey === 'processingTimeInMiliseconds'
                    );
                    const stepsData = Object.keys(steptsDict).map((key, _) => payload.find((p) => p.dataKey === key));
                    return (
                      <div
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '6px',
                            borderBottom: '1px solid #e0e0e0',
                            paddingBottom: '4px',
                          }}
                        >
                          {new Date(label ?? 0).toLocaleString()}
                        </div>
                        {processingTimeInMiliseconds && (
                          <div
                            style={{ fontSize: '12px', color: processingTimeInMiliseconds.color, marginBottom: '3px' }}
                          >
                            <span style={{ fontWeight: '500' }}>{`${processingTimeInMiliseconds.name}:`}</span>{' '}
                            {Common.Utils.getComponentTimestampFromSeconds(
                              Math.round(Number(processingTimeInMiliseconds.value) / 1000)
                            )}
                          </div>
                        )}
                        {stepsData.map(
                          (value, index) =>
                            value && (
                              <div key={index} style={{ fontSize: '12px', color: value.color, marginBottom: '3px' }}>
                                <span style={{ fontWeight: '500' }}>{`${value.name}:`}</span>{' '}
                                {Common.Utils.getComponentTimestampFromSeconds(Math.round(Number(value.value) / 1000))}
                              </div>
                            )
                        )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      );
    },
    [
      dataStartDate,
      dataEndDate,
      showProcessingTimeInMiliseconds,
      showGetUniqueNormalizedProductsStat,
      showSynchronizeProductsStat,
      showSynchronizeCategoriesStat,
      showSynchronizeLocationsProductInfoStat,
      setShowProcessingTimeInMiliseconds,
      setShowGetUniqueNormalizedProductsStat,
      setShowSynchronizeProductsStat,
      setShowSynchronizeCategoriesStat,
      setShowSynchronizeLocationsProductInfoStat,
    ]
  );

  const generateDataFeedsCollectingTimeChart = useCallback(
    (data: IScraperDataFeed[]) => {
      const webcrawlerCollectingTotalTimeInMillisecondsColor = '#100e92ff';
      const webcrawlerCollectingAbsoluteTimeInMillisecondsColor = '#b30f46ff';
      const webcrawlerCollectingAverageTimeInMillisecondsColor = '#ffa600';
      const RenderChartLegend = () => (
        <Container>
          <Row xs='auto' className='justify-content-center align-items-center'>
            <Col>
              <Form.Check
                id='show-collecting-total-time-switch'
                label={<div style={{ color: webcrawlerCollectingTotalTimeInMillisecondsColor }}>Suma</div>}
                style={{ userSelect: 'none' }}
                checked={showWebcrawlerCollectingTotalTimeInMilliseconds}
                onChange={() => {
                  setShowWebcrawlerCollectingTotalTimeInMilliseconds(!showWebcrawlerCollectingTotalTimeInMilliseconds);
                }}
              />
            </Col>
            <Col>
              <Form.Check
                id='show-collecting-absolute-time-switch'
                label={<div style={{ color: webcrawlerCollectingAbsoluteTimeInMillisecondsColor }}>Całkowity</div>}
                style={{ userSelect: 'none' }}
                checked={showWebcrawlerCollectingAbsoluteTimeInMilliseconds}
                onChange={() => {
                  setShowWebcrawlerCollectingAbsoluteTimeInMilliseconds(
                    !showWebcrawlerCollectingAbsoluteTimeInMilliseconds
                  );
                }}
              />
            </Col>
            <Col>
              <Form.Check
                id='show-collecting-average-time-switch'
                label={<div style={{ color: webcrawlerCollectingAverageTimeInMillisecondsColor }}>Średni</div>}
                style={{ userSelect: 'none' }}
                checked={showWebcrawlerCollectingAverageTimeInMilliseconds}
                onChange={() => {
                  setShowWebcrawlerCollectingAverageTimeInMilliseconds(
                    !showWebcrawlerCollectingAverageTimeInMilliseconds
                  );
                }}
              />
            </Col>
          </Row>
        </Container>
      );
      data = data.filter(
        (d) =>
          !!d.webcrawlerCollectingStartDate &&
          new Date(d.webcrawlerCollectingStartDate!) >= dataStartDate &&
          new Date(d.webcrawlerCollectingStartDate!) <= dataEndDate
      );
      const chartData = data.map((element) => {
        const result = {
          date: new Date(element.webcrawlerCollectingStartDate!),
          webcrawlerCollectingTotalTimeInMilliseconds: element.webcrawlerCollectingTotalTimeInMilliseconds,
          webcrawlerCollectingAbsoluteTimeInMilliseconds: element.webcrawlerCollectingAbsoluteTimeInMilliseconds,
          webcrawlerCollectingAverageTimeInMilliseconds: element.webcrawlerCollectingAverageTimeInMilliseconds,
        };
        return result;
      });
      return (
        <div className='overflow-auto'>
          <ResponsiveContainer minWidth={600} width='100%' height={400}>
            <ComposedChart
              accessibilityLayer={false}
              data={chartData}
              margin={{
                left: 10,
                right: 25,
                top: 5,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray='1' />
              <XAxis dataKey={'date'} name={'Dzień'} tickFormatter={(e) => Common.Utils.getDateString(new Date(e))} />
              <YAxis
                width={'auto'}
                tickFormatter={(val) => Common.Utils.getComponentTimestampFromSeconds(Math.round(Number(val) / 1000))}
              />
              {showWebcrawlerCollectingTotalTimeInMilliseconds && (
                <Line
                  type='monotone'
                  dataKey='webcrawlerCollectingTotalTimeInMilliseconds'
                  name='Czas pobierania rekordów'
                  activeDot={{ r: 6 }}
                  stroke={webcrawlerCollectingTotalTimeInMillisecondsColor}
                  connectNulls={true}
                />
              )}
              {showWebcrawlerCollectingAbsoluteTimeInMilliseconds && (
                <Line
                  type='monotone'
                  dataKey='webcrawlerCollectingAbsoluteTimeInMilliseconds'
                  name='Całkowity czas pobierania rekordów'
                  activeDot={{ r: 6 }}
                  stroke={webcrawlerCollectingAbsoluteTimeInMillisecondsColor}
                  connectNulls={true}
                />
              )}
              {showWebcrawlerCollectingAverageTimeInMilliseconds && (
                <Line
                  type='monotone'
                  dataKey='webcrawlerCollectingAverageTimeInMilliseconds'
                  name='Średni czas pobierania rekordów'
                  activeDot={{ r: 6 }}
                  stroke={webcrawlerCollectingAverageTimeInMillisecondsColor}
                  connectNulls={true}
                />
              )}
              <Legend content={<RenderChartLegend />} verticalAlign='top' iconSize={10} height={36} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const webcrawlerCollectingTotalTimeInMilliseconds = payload.find(
                      (p) => p.dataKey === 'webcrawlerCollectingTotalTimeInMilliseconds'
                    );
                    const webcrawlerCollectingAbsoluteTimeInMilliseconds = payload.find(
                      (p) => p.dataKey === 'webcrawlerCollectingAbsoluteTimeInMilliseconds'
                    );
                    const webcrawlerCollectingAverageTimeInMilliseconds = payload.find(
                      (p) => p.dataKey === 'webcrawlerCollectingAverageTimeInMilliseconds'
                    );
                    return (
                      <div
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '6px',
                            borderBottom: '1px solid #e0e0e0',
                            paddingBottom: '4px',
                          }}
                        >
                          {new Date(label ?? 0).toLocaleString()}
                        </div>
                        {webcrawlerCollectingTotalTimeInMilliseconds && (
                          <div
                            style={{
                              fontSize: '12px',
                              color: webcrawlerCollectingTotalTimeInMilliseconds.color,
                              marginBottom: '3px',
                            }}
                          >
                            <span
                              style={{ fontWeight: '500' }}
                            >{`${webcrawlerCollectingTotalTimeInMilliseconds.name}:`}</span>{' '}
                            {Common.Utils.getComponentTimestampFromSeconds(
                              Math.round(Number(webcrawlerCollectingTotalTimeInMilliseconds.value) / 1000)
                            )}
                          </div>
                        )}
                        {webcrawlerCollectingAbsoluteTimeInMilliseconds && (
                          <div
                            style={{
                              fontSize: '12px',
                              color: webcrawlerCollectingAbsoluteTimeInMilliseconds.color,
                              marginBottom: '3px',
                            }}
                          >
                            <span
                              style={{ fontWeight: '500' }}
                            >{`${webcrawlerCollectingAbsoluteTimeInMilliseconds.name}:`}</span>{' '}
                            {Common.Utils.getComponentTimestampFromSeconds(
                              Math.round(Number(webcrawlerCollectingAbsoluteTimeInMilliseconds.value) / 1000)
                            )}
                          </div>
                        )}
                        {webcrawlerCollectingAverageTimeInMilliseconds && (
                          <div
                            style={{
                              fontSize: '12px',
                              color: webcrawlerCollectingAverageTimeInMilliseconds.color,
                              marginBottom: '3px',
                            }}
                          >
                            <span
                              style={{ fontWeight: '500' }}
                            >{`${webcrawlerCollectingAverageTimeInMilliseconds.name}:`}</span>{' '}
                            {Common.Utils.getComponentTimestampFromSeconds(
                              Math.round(Number(webcrawlerCollectingAverageTimeInMilliseconds.value) / 1000)
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      );
    },
    [
      dataStartDate,
      dataEndDate,
      showWebcrawlerCollectingTotalTimeInMilliseconds,
      showWebcrawlerCollectingAbsoluteTimeInMilliseconds,
      showWebcrawlerCollectingAverageTimeInMilliseconds,
      setShowWebcrawlerCollectingTotalTimeInMilliseconds,
      setShowWebcrawlerCollectingAbsoluteTimeInMilliseconds,
      setShowWebcrawlerCollectingAverageTimeInMilliseconds,
    ]
  );

  const dataFeedCount = scraperDetails?.dataFeeds.length ?? 0;
  const dataFeedFailuresCount = scraperDetails?.dataFeeds.filter((df) => !df.completionDate).length ?? 0;

  return (
    <>
      {renderBreadcrumb()}
      <h4>Statystyki zbierania danych w {scraperDetails?.locationName}</h4>
      <div className='table-responsive-sm'>
        <table className='table '>
          <thead className='thead-dark'>
            <tr>
              <th scope='col' style={{ width: '30%' }}>
                Parametr
              </th>
              <th scope='col'>Wartość</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Nazwa firmy</td>
              <td>{!scraperDetails && loading ? <Skeleton /> : scraperDetails?.companyName}</td>
            </tr>
            <tr>
              <td>Id domyślnej lokalizacji</td>
              <td>{!scraperDetails && loading ? <Skeleton /> : scraperDetails?.companyDefaultLocationId}</td>
            </tr>
            <tr>
              <td>Adres url</td>
              <td>{!scraperDetails && loading ? <Skeleton /> : scraperDetails?.urlHostPath}</td>
            </tr>
            <tr>
              <td>Harmonogram</td>
              <td>{!scraperDetails && loading ? <Skeleton /> : scraperDetails?.cronSchedule}</td>
            </tr>
            <tr>
              <td>Liczba przetworzeń</td>
              <td>{!scraperDetails && loading ? <Skeleton /> : dataFeedCount}</td>
            </tr>
            <tr>
              <td>Liczba nieudanych przetworzeń</td>
              <td>
                {!scraperDetails && loading ? (
                  <Skeleton />
                ) : (
                  `${dataFeedFailuresCount} (${Math.round((10000 * dataFeedFailuresCount) / dataFeedCount) / 100}%)`
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        style={{
          paddingTop: '10px',
          paddingBottom: '10px',
          borderRadius: '5px',
          backgroundColor: '#00aa5b',
          marginBottom: '15px',
          color: 'white',
        }}
      >
        {renderLegend(loading)}
      </div>

      <Card>
        {Common.Ui.showLoadingSpinnerAbsolute(loading)}
        <Card.Header className='statistics-card-header'>Liczba przetworzonych rekordów</Card.Header>
        <Card.Body>{generateDataFeedsCounterChart(scraperDetails?.dataFeeds ?? [])}</Card.Body>
      </Card>
      <Card className='my-3'>
        {Common.Ui.showLoadingSpinnerAbsolute(loading)}
        <Card.Header className='statistics-card-header'>Czas przetwarzania danych</Card.Header>
        <Card.Body>{generateDataFeedsTimeChart(scraperDetails?.dataFeeds ?? [])}</Card.Body>
      </Card>
      <Card className='my-3'>
        {Common.Ui.showLoadingSpinnerAbsolute(loading)}
        <Card.Header className='statistics-card-header'>Czas pobierania danych</Card.Header>
        <Card.Body>{generateDataFeedsCollectingTimeChart(scraperDetails?.dataFeeds ?? [])}</Card.Body>
      </Card>
    </>
  );
};

export default ScraperDetails;
